import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, qs, unwrap } from '../api/client';
import { useAccounts, useAutomations } from '../api/hooks';
import type { Insights, Items, Media, MediaStats, Post } from '../api/types';
import {
  Badge,
  Card,
  DataTable,
  ErrorAlert,
  fmtDate,
  fmtPct,
  PageHeader,
  Stat,
} from '../components/ui';
import { LineChart } from './Dashboard';
import { automationsForMedia } from './insights-helpers';

/** ANA-11: Instagram stories live 24 hours. */
export const storyStatus = (publishedAt: string | null): string =>
  publishedAt && Date.now() - new Date(publishedAt).getTime() < 24 * 3600 * 1000
    ? 'active'
    : 'expired';

export function InsightsPage() {
  const [tab, setTab] = useState<'posts' | 'stories' | 'dm'>('dm');
  const [days, setDays] = useState<7 | 15 | 30>(7);
  const ins = useQuery({
    queryKey: ['insights', days],
    queryFn: () => unwrap(api.get<Insights>(`/portal/analytics/insights${qs({ days })}`)),
  });
  // ANA-12: per-media figures from the ledgers, never a sum over every bound automation
  // the range buttons apply to these tables too (v27)
  const from = new Date(Date.now() - days * 86_400_000).toISOString();
  const postStats = useQuery({
    queryKey: ['media-stats', 'post', days],
    queryFn: () =>
      unwrap(api.get<Items<MediaStats>>(`/portal/analytics/media${qs({ kind: 'post', from })}`)),
  });
  const storyStats = useQuery({
    queryKey: ['media-stats', 'story', days],
    queryFn: () =>
      unwrap(api.get<Items<MediaStats>>(`/portal/analytics/media${qs({ kind: 'story', from })}`)),
  });
  const EMPTY: MediaStats = {
    mediaExternalId: '',
    interactions: 0,
    uniqueUsers: 0,
    runs: 0,
    dmsSent: 0,
    clicks: 0,
    conversions: 0,
  };
  // UI-30: media insights are a Legend benefit; say so instead of showing zeros
  const mediaGated =
    (postStats.error as { code?: string } | null)?.code === 'plan_feature_unavailable' ||
    (storyStats.error as { code?: string } | null)?.code === 'plan_feature_unavailable';
  const dmGated = (ins.error as { code?: string } | null)?.code === 'plan_feature_unavailable';
  const statsOf = (kind: 'post' | 'story', externalId: string): MediaStats =>
    (kind === 'post' ? postStats : storyStats).data?.items.find(
      (x) => x.mediaExternalId === externalId,
    ) ?? EMPTY;
  const automations = useAutomations();
  const posts = useQuery({
    queryKey: ['posts', {}],
    queryFn: () => unwrap(api.get<{ items: Post[] }>('/portal/posts')),
  });
  const accounts = useAccounts();
  const media = useQuery({
    queryKey: ['all-media', accounts.data?.items.map((a) => a.id)],
    queryFn: async () =>
      (
        await Promise.all(
          (accounts.data?.items ?? []).map((a) =>
            unwrap(api.get<Items<Media>>(`/portal/social-accounts/${a.id}/media`)).then(
              (r) => r.items,
            ),
          ),
        )
      ).flat(),
    enabled: (accounts.data?.items.length ?? 0) > 0,
  });
  const boundTo = (externalId: string, kind: 'story' | 'feed') =>
    automationsForMedia(automations.data?.items ?? [], externalId, kind);
  const i = ins.data;
  // UI-62: the PRD's Posts tab is "posts for which the automation is used"; the toggle shows the rest
  const [showAll, setShowAll] = useState(false);
  const rows = (kind: 'story' | 'feed') =>
    (media.data ?? [])
      .filter((m) => (kind === 'story' ? m.kind === 'story' : m.kind !== 'story'))
      .map((m) => ({
        m,
        autos: boundTo(m.externalId, kind),
        post: posts.data?.items.find((p) => p.externalMediaId === m.externalId),
      }))
      .filter(({ autos }) => kind === 'story' || showAll || autos.length > 0);
  return (
    <>
      <PageHeader
        title="DM Insights"
        subtitle="Performance of posts, stories and DMs"
        actions={
          <div className="actions">
            {([7, 15, 30] as const).map((d) => (
              <button
                type="button"
                key={d}
                className={`btn btn-sm ${days === d ? 'btn-primary' : ''}`}
                onClick={() => setDays(d)}
              >
                {d}D
              </button>
            ))}
          </div>
        }
      />
      <div className="tabs">
        {(
          [
            ['posts', 'Posts'],
            ['stories', 'Stories'],
            ['dm', 'DM insights'],
          ] as const
        ).map(([k, l]) => (
          <button
            type="button"
            key={k}
            className={tab === k ? 'active' : ''}
            onClick={() => setTab(k)}
          >
            {l}
          </button>
        ))}
      </div>
      {/* UI-33 / UI-36: plan gates have their own notices; every other error shows raw */}
      <ErrorAlert error={dmGated ? null : ins.error} />
      <ErrorAlert error={mediaGated ? null : (postStats.error ?? storyStats.error)} />
      {tab === 'dm' && dmGated ? (
        <div className="alert alert-info" data-testid="dm-gated">
          DM insights are part of the Legend plan and above. Upgrade in Settings to see them here.
        </div>
      ) : null}
      {tab === 'dm' && i ? (
        <>
          <div className="card-title">Overview</div>
          <div className="grid grid-4">
            <Stat label="DM sent" value={i.overview.dmSent} />
            <Stat label="Link clicks" value={i.overview.linkClicks} />
            <Stat label="CTR" value={fmtPct(i.overview.ctr)} />
            <Stat label="Active conversations" value={i.overview.activeConversations} />
          </div>
          <div className="card-title" style={{ marginTop: 16 }}>
            Conversions
          </div>
          <div className="grid grid-4">
            <Stat label="Total conversions" value={i.conversions.total} />
            <Stat label="Unread DMs" value={i.conversions.unreadDms} />
            <Stat label="Followers converted" value={i.conversions.followers} />
            <Stat label="Non-followers converted" value={i.conversions.nonFollowers} />
            <Stat label="Follower status unknown" value={i.conversions.unknown} />
          </div>
          <div className="grid grid-2" style={{ marginTop: 16 }}>
            <Card title="Daily click trend">
              <LineChart
                labels={i.clicks.daily.map((p) => p.day)}
                series={[
                  { name: 'Clicks', color: '#b45309', values: i.clicks.daily.map((p) => p.clicks) },
                  {
                    name: 'DMs delivered',
                    color: '#15803d',
                    values: i.clicks.daily.map((p) => p.delivered),
                  },
                ]}
              />
              <div className="small muted">
                Total {i.clicks.total} · unique {i.clicks.unique}
              </div>
            </Card>
            <Card title="Usage over time (clicks per hour of day, UTC)">
              <LineChart
                labels={(i.clicks.byHour ?? []).map((h) => `  ${String(h.hour).padStart(2, '0')}h`)}
                series={[
                  {
                    name: 'Clicks',
                    color: '#5b5bd6',
                    values: (i.clicks.byHour ?? []).map((h) => h.clicks),
                  },
                ]}
              />
            </Card>
            <Card title="Top 5 click locations">
              <DataTable>
                <table className="table">
                  <tbody>
                    {(i.clicks.locations ?? []).map((l) => (
                      <tr key={`${l.city ?? ''}|${l.country}`}>
                        {/* GEO-04 */}
                        <td>{l.city ? `${l.city}, ${l.country}` : l.country}</td>
                        <td className="right">{l.clicks}</td>
                      </tr>
                    ))}
                    {(i.clicks.locations ?? []).length === 0 ? (
                      <tr>
                        <td className="muted">No clicks yet</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </DataTable>
            </Card>
          </div>
        </>
      ) : null}
      {tab === 'posts' ? (
        <DataTable>
          <label className="row small" style={{ alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <input
              type="checkbox"
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
            />
            Show all synced posts
          </label>
          {mediaGated ? (
            <div className="alert alert-info" data-testid="media-gated">
              Per-post and per-story insights are part of the Legend plan and above. Upgrade in
              Settings to see them here.
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Post</th>
                  <th>Type</th>
                  <th>Published</th>
                  <th>Likes</th>
                  <th>Comments</th>
                  <th>Automations</th>
                  <th>Users</th>
                  <th>DM sent</th>
                  <th>Clicks</th>
                  <th>Conversions</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows('feed').map(({ m, autos, post }) => (
                  <tr key={m.id}>
                    <td>
                      <div className="row">
                        {m.thumbnailUrl ? (
                          <img
                            src={m.thumbnailUrl}
                            alt=""
                            width={40}
                            height={40}
                            style={{ borderRadius: 6 }}
                          />
                        ) : (
                          <span className="thumb-placeholder" aria-hidden="true" />
                        )}
                        <div>
                          <span className="mono">{m.externalId}</span>
                          <div className="small muted truncate">{m.caption}</div>
                        </div>
                      </div>
                    </td>
                    <td>{m.kind}</td>
                    <td>{fmtDate(m.publishedAt)}</td>
                    <td>{m.likeCount ?? '—'}</td>
                    <td>{m.commentsCount ?? '—'}</td>
                    <td>{autos.map((a) => a.name).join(', ') || '—'}</td>
                    <td>{statsOf('post', m.externalId).uniqueUsers}</td>
                    <td>{statsOf('post', m.externalId).dmsSent}</td>
                    <td>{statsOf('post', m.externalId).clicks}</td>
                    <td>{statsOf('post', m.externalId).conversions}</td>
                    <td>
                      {post?.status ??
                        (autos.some((a) => a.status === 'active') ? 'active' : 'no automation')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </DataTable>
      ) : null}
      {tab === 'stories' ? (
        <DataTable>
          {mediaGated ? (
            <div className="alert alert-info" data-testid="media-gated">
              Per-post and per-story insights are part of the Legend plan and above. Upgrade in
              Settings to see them here.
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Story</th>
                  <th>Published</th>
                  <th>Status</th>
                  <th>Views</th>
                  <th>Automations</th>
                  <th>Replies (users)</th>
                  <th>DM sent</th>
                  <th>Clicks</th>
                  <th>Conversions</th>
                </tr>
              </thead>
              <tbody>
                {rows('story').map(({ m, autos }) => {
                  const sa = autos.filter((a) => a.triggerType === 'story_reply');
                  return (
                    <tr key={m.id}>
                      <td>
                        <div className="row">
                          {m.thumbnailUrl ? (
                            <img
                              src={m.thumbnailUrl}
                              alt=""
                              width={40}
                              height={40}
                              style={{ borderRadius: 6 }}
                            />
                          ) : (
                            <span className="thumb-placeholder" aria-hidden="true" />
                          )}
                          <span className="mono">{m.externalId}</span>
                        </div>
                      </td>
                      <td>{fmtDate(m.publishedAt)}</td>
                      <td>
                        <Badge value={storyStatus(m.publishedAt)} />
                      </td>
                      <td>{m.viewCount ?? '—'}</td>
                      <td>{sa.map((a) => a.name).join(', ') || '—'}</td>
                      <td>{statsOf('story', m.externalId).uniqueUsers}</td>
                      <td>{statsOf('story', m.externalId).dmsSent}</td>
                      <td>{statsOf('story', m.externalId).clicks}</td>
                      <td>{statsOf('story', m.externalId).conversions}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </DataTable>
      ) : null}
    </>
  );
}
